import { type CycleReminderInfo, type CycleReminderType, type ReviewCycleDetail, REMINDER_MESSAGE_MAX_LENGTH } from "#shared/reviewCycleSchemas.ts";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Checkbox, CheckboxField, CheckboxGroup } from "@/components/ui/checkbox.tsx";
import { Label } from "@/components/ui/field.tsx";
import { Loader } from "@/components/ui/loader.tsx";
import { Modal, ModalBody, ModalContent, ModalDescription, ModalFooter, ModalHeader, ModalTitle } from "@/components/ui/modal.tsx";
import { Note } from "@/components/ui/note.tsx";
import { TextField } from "@/components/ui/text-field.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { PaperAirplaneIcon, UsersIcon } from "@heroicons/react/24/outline";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { PersonAvatar } from "@packages/core-client/people/PersonAvatar.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useReviewCyclesClient, useSendCycleRemindersMutationOptions } from "../../../api/reviewCyclesQuery.ts";
import { reviewCyclesI18NextInstanceKey } from "../../../i18n/i18next.ts";
import { useRevieweeFilters } from "../../reviewee-filters/useRevieweeFilters.ts";
import { CycleNudgeRecipientsPopover } from "./CycleNudgeRecipientsPopover.tsx";
import { toNudgeDueCountdown } from "./nudgeDueCountdown.ts";

const REVIEW_TYPE_BY_REMINDER = new Map<CycleReminderType, string>([
    ["self-review", "self"],
    ["manager-review", "manager"],
    ["peer-review", "peer"],
    ["upward-review", "upward"]
]);

export interface CycleNudgeDialogProps {
    cycle: ReviewCycleDetail;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
}

export function CycleNudgeDialog({ cycle, isOpen, onOpenChange }: CycleNudgeDialogProps) {
    const i18nextInstance = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t } = useTranslation("CycleNudgeDialog", { i18n: i18nextInstance });
    const { queryUtils } = useReviewCyclesClient();
    const session = useSession();

    const sender = useMemo(() => {
        const displayName = session?.displayName ?? session?.firstName;

        return session && displayName ? { id: session.memberId, displayName, imageUrl: session.image } : undefined;
    }, [session]);

    const [now] = useState(() => new Date());

    const [selectedTypes, setSelectedTypes] = useState<CycleReminderType[]>([]);
    const [message, setMessage] = useState("");

    const { memberFilters } = useRevieweeFilters();

    const scopedInput = useMemo(() => ({ orgId: session?.organizationId ?? "", cycleId: cycle.id, ...memberFilters }), [session?.organizationId, cycle.id, memberFilters]);

    const informationOptions = queryUtils.getCycleReminderInformation.queryOptions({ input: scopedInput });

    const { data: scopedProgress } = useQuery(createQueryOptions(queryUtils.getCycleProgress.queryOptions({ input: scopedInput }), { enabled: isOpen, throwOnError: false }));

    const { data, isPending, isError } = useQuery(createQueryOptions(informationOptions, { enabled: isOpen, throwOnError: false }));

    const { mutateAsync: sendReminders, isPending: isSending } = useMutation(useSendCycleRemindersMutationOptions());

    const reminders = useMemo(() => data?.reminders ?? [], [data]);

    const dueDateByReminder = useMemo(() => {
        const dates = new Map<CycleReminderType, string>();

        for (const [reminderType, reviewType] of REVIEW_TYPE_BY_REMINDER) {
            const dueDate = scopedProgress?.progress?.find(entry => entry.reviewType === reviewType)?.dueDate;

            if (dueDate) {
                dates.set(reminderType, dueDate);
            }
        }

        return dates;
    }, [scopedProgress]);

    const recipientCount = useMemo(() => new Set(reminders.filter(reminder => selectedTypes.includes(reminder.reminderType)).flatMap(reminder => reminder.recipientIds)).size, [reminders, selectedTypes]);

    const sentRecentlyLabels = useMemo(() => reminders.filter(reminder => selectedTypes.includes(reminder.reminderType) && reminder.wasSentRecently).map(reminder => t(`reminderType.${reminder.reminderType}`)), [reminders, selectedTypes, t]);

    const recentlySentWarning = t("recentlySent", { count: sentRecentlyLabels.length, types: sentRecentlyLabels.join(", "), hours: data?.recentWindowHours ?? 0 });

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                setSelectedTypes([]);
                setMessage("");
            }

            onOpenChange(open);
        },
        [onOpenChange]
    );

    const handleCancel = useCallback(() => handleOpenChange(false), [handleOpenChange]);

    const handleSelectionChange = useCallback((values: string[]) => setSelectedTypes(reminders.map(reminder => reminder.reminderType).filter(reminderType => values.includes(reminderType))), [reminders]);

    const orgId = session?.organizationId ?? "";
    const cycleId = cycle.id;

    const handleSend = useCallback(async () => {
        try {
            await sendReminders({ orgId, cycleId, reminderTypes: selectedTypes, message: message.trim() || undefined, filters: memberFilters });
            toast.success(t("sentToast", { count: recipientCount }), { position: "top-right" });
            handleOpenChange(false);
        } catch {
            toast.error(t("sendFailedToast"), { position: "top-right" });
        }
    }, [sendReminders, orgId, cycleId, selectedTypes, message, memberFilters, recipientCount, handleOpenChange, t]);

    function renderRow(reminder: CycleReminderInfo) {
        const countdown = toNudgeDueCountdown(dueDateByReminder.get(reminder.reminderType), cycle.keepsAdmitting, now);
        const hasRecipients = reminder.recipientIds.length > 0;

        return (
            <div key={reminder.reminderType} className="border-border flex items-center gap-4 px-5 py-4 not-first:border-t">
                <CheckboxField className="min-w-0 flex-1" value={reminder.reminderType} isDisabled={!hasRecipients}>
                    <Checkbox>{t(`nudgeTo.${reminder.reminderType}`)}</Checkbox>
                </CheckboxField>

                <span className="text-muted-fg flex shrink-0 items-center gap-1.5">
                    <UsersIcon className="size-4 shrink-0" aria-hidden="true" />
                    {hasRecipients ? <CycleNudgeRecipientsPopover cycleId={cycle.id} memberIds={reminder.recipientIds} /> : <span className="text-sm">{t("recipients.none")}</span>}
                </span>

                <span className="flex min-w-28 shrink-0 justify-end">
                    {reminder.wasSentRecently ? (
                        <Badge intent="warning">{t("sentRecentlyBadge")}</Badge>
                    ) : (
                        countdown && <Badge intent={countdown.kind === "overdue" ? "danger" : "secondary"}>{countdown.kind === "today" ? t("due.today") : t(countdown.kind === "overdue" ? "due.overdue" : "due.daysLeft", { count: countdown.days })}</Badge>
                    )}
                </span>
            </div>
        );
    }

    return (
        <Modal isOpen={isOpen} onOpenChange={handleOpenChange}>
            <ModalContent size="2xl">
                <ModalHeader>
                    <ModalTitle>{t("title")}</ModalTitle>
                    <ModalDescription>{t("description")}</ModalDescription>
                </ModalHeader>

                <ModalBody className="flex flex-col gap-6">
                    {isPending && (
                        <div className="flex items-center gap-2">
                            <div aria-hidden="true">
                                <Loader />
                            </div>
                            <span className="text-muted-fg text-sm">{t("loading")}</span>
                        </div>
                    )}

                    {isError && <Note intent="danger">{t("loadFailed")}</Note>}

                    {!isPending && !isError && reminders.length === 0 && <p className="text-muted-fg text-sm">{t("nothingToRemind")}</p>}

                    {reminders.length > 0 && (
                        <CheckboxGroup value={selectedTypes} onChange={handleSelectionChange} className="space-y-3">
                            <Label className="text-muted-fg text-xs tracking-widest uppercase">{t("nudgeToHeading")}</Label>
                            <div className="border-border rounded-xl border">{reminders.map(renderRow)}</div>
                        </CheckboxGroup>
                    )}

                    <output className="sr-only">{sentRecentlyLabels.length > 0 ? recentlySentWarning : ""}</output>
                    {sentRecentlyLabels.length > 0 && <Note intent="warning">{recentlySentWarning}</Note>}

                    <div className="flex flex-col gap-3">
                        <TextField className="space-y-3" value={message} onChange={setMessage} maxLength={REMINDER_MESSAGE_MAX_LENGTH}>
                            <Label className="text-muted-fg text-xs tracking-widest uppercase">{t("message.label")}</Label>
                            <Textarea rows={4} placeholder={t("message.placeholder")} />
                        </TextField>

                        {sender && (
                            <p className="text-muted-fg flex items-center gap-1.5 text-sm">
                                <span>{t("message.sentAs")}</span>
                                <PersonAvatar person={sender} size="xs" />
                                <span className="text-fg font-semibold">{sender.displayName}</span>
                            </p>
                        )}
                    </div>
                </ModalBody>

                <ModalFooter>
                    <Button intent="outline" onPress={handleCancel} isDisabled={isSending}>
                        {t("cancel")}
                    </Button>
                    <Button intent="primary" onPress={handleSend} isPending={isSending} isDisabled={selectedTypes.length === 0}>
                        {isSending ? <Loader aria-label={t("sending")} /> : <PaperAirplaneIcon aria-hidden="true" />}
                        {selectedTypes.length > 0 ? t("sendToCount", { count: recipientCount }) : t("send")}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
